import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'src/rerouter/index';
import {
  userSignUp,
  USER_SIGNUP_ERROR,
} from 'src/action-creators/referrer-portal/authentication';
import {CONSTANTS} from 'src/components/authentication/contants';
import {ERROR_CODES} from 'src/components/authentication/errors';

import * as reduxApi from 'src/utils/redux-api-referrer-portal';
import {validateEmail} from 'src/utils/index';

import {
  TitleMedium,
  BodyMedium,
  ButtonTextMedium,
  Input,
  Button,
  InContextAlert,
  BodyLarge,
  Tooltip,
} from '@spaced-out/ui-design-system/lib/components/index';
import PasswordConfirmation from 'src/components/authentication/common/password-confirmation/password-confirmation';
import GoogleIcon from 'src/images/google-icon.svg';

import css from './sign-up.css';
import maincss from 'src/components/authentication/main.css';


const SignUpForm = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const {t, makeKey} = useLabelTranslation();

  const {location} = history;
  const {
    query: {email: defualtEmail = ''},
  } = location;

  const signUpError = useSelector((state) => state.referrerPortal.signup_error);

  const [name, setName] = React.useState<string>('');
  const [email, setEmail] = React.useState<string>(
    decodeURIComponent(defualtEmail),
  );
  const [newPassword, setNewPassword] = React.useState<string>('');
  const [confirmPassword, setConfirmPassword] = React.useState<string>('');
  const [passwordStrength, setPasswordStrength] = React.useState<number>(0);
  const [isSigningUp, setIsSigningUp] = React.useState<boolean>(false);
  const [touched, setTouched] = React.useState<{
    [key: string]: boolean;
  }>({});

  const goToSignIn = () => {
    dispatch({type: USER_SIGNUP_ERROR, payload: null});
    history.replace({
      ...history.location,
      pathname: `/signin`,
      query: location.query,
    });
  };

  const handleSignUp = async () => {
    setIsSigningUp(true);
    dispatch(
      userSignUp({
        name,
        email,
        password: newPassword,
      }),
    ).then((response) => {
      //@ts-expect-error
      if (!response) {
        setIsSigningUp(false);
      }
    });
  };

  // @ts-expect-error - TS7030 - Not all code paths return a value.
  const isError = () => {
    if (!name || !email || !newPassword) {
      return true;
    }
    if (!validateEmail(email)) {
      return true;
    }
    if (newPassword !== confirmPassword || passwordStrength < 3) {
      return true;
    }
  };

  const handleGoogleSignUp = () => {
    dispatch(reduxApi.get('auth/login/google')).then((googleAuthUrl) =>
      window.open(googleAuthUrl, '_self'),
    );
  };

  return (
    <div className={maincss.contentSideBar}>
      <div className={css.signUpcontainerContent}>
        <div className={css.signUpSection}>
          <TitleMedium className={css.contentTitle}>{`${t(
            'Hi_welcome_to_Sense_Referrals',
            'Hi, welcome to Sense Referrals',
          )}!`}</TitleMedium>
          <div className={css.input}>
            <Button
              onClick={handleGoogleSignUp}
              classNames={{wrapper: css.googleBtn}}
              type="tertiary"
            >
              <div className={css.googleBtnContent}>
                <GoogleIcon />
                <ButtonTextMedium>
                  {t('Sign_up_with_Google', 'Sign up with Google')}
                </ButtonTextMedium>
              </div>
            </Button>
          </div>
          <div className={css.input}>
            <div className={css.subHeaderContainer}>
              <div className={css.subHeaderBorder} />
              <BodyMedium className={css.subHeaderText} color="secondary">
                {t(
                  'or_enter_the_details_below_to_sign_up',
                  'or enter the details below to sign up',
                )}
              </BodyMedium>
              <div className={css.subHeaderBorder} />
            </div>
          </div>
          <div className={css.input}>
            <Input
              label={t('Name', 'Name')}
              placeholder={t('Enter_full_name', 'Enter full name')}
              size="medium"
              required={true}
              type="text"
              value={name}
              error={touched['name'] && !name}
              errorText={t(
                makeKey(CONSTANTS.INVALID_FULL_NAME),
                CONSTANTS.INVALID_FULL_NAME,
              )}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setTouched({...touched, name: true})}
            />
          </div>
          <div className={css.input}>
            <Input
              label={t('Email', 'Email')}
              placeholder={t('Enter_email', 'Enter email')}
              size="medium"
              required={true}
              type="text"
              value={email}
              error={touched['email'] && !validateEmail(email)}
              errorText={t(
                makeKey(CONSTANTS.INVALID_EMAIL),
                CONSTANTS.INVALID_EMAIL,
              )}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched({...touched, email: true})}
              disabled={!!defualtEmail}
            />
          </div>
          <PasswordInstruction />
          <div className={css.confirmation}>
            <PasswordConfirmation
              passwordLabel={t('New_password', 'New password')}
              passwordPlaceholder={t('Enter_password', 'Enter password')}
              confirmPasswordLabel={t('Confirm_password', 'Confirm password')}
              confirmPasswordPlaceholder={t(
                'Repeat_password',
                'Repeat password',
              )}
              setPasswordStrengthValue={setPasswordStrength}
              setNewPasswordValue={setNewPassword}
              setConfirmPasswordValue={setConfirmPassword}
              touched={touched}
              setTouched={setTouched}
            />
          </div>
          <Button
            size="medium"
            type="primary"
            classNames={{wrapper: css.signUpBtn}}
            disabled={isError() || isSigningUp}
            onClick={handleSignUp}
          >
            {isSigningUp
              ? t('Signing_up', 'Signing up')
              : t('Sign_up', 'Sign up')}
          </Button>
          {signUpError && (
            <InContextAlert semantic="danger" classNames={{wrapper: css.alert}}>
              {/* @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly InvalidLoginAttempt: "Incorrect email or password"; readonly ServerError: "Failed to perform action, Please try after sometime"; readonly IncorrectOTP: "Invalid OTP entered"; ... 13 more ...; readonly InvalidReferrerEmail: "Invalid Referrer Email"; }'. */}
              {ERROR_CODES[signUpError] || signUpError}
            </InContextAlert>
          )}
          <div className={css.signInSection}>
            <BodyMedium className={css.forgotLoginText}>
              {t('Or', 'Or')}
            </BodyMedium>
            <ButtonTextMedium
              color="clickable"
              className={css.signInLink}
              // @ts-expect-error - TS2769 - No overload matches this call.
              onClick={goToSignIn}
            >
              {t('Sign_in', 'Sign in')}
            </ButtonTextMedium>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpForm;

export const PasswordInstruction = () => {
  const {t} = useLabelTranslation();
  return (
    <div className={css.instructions}>
      <Tooltip
        body={`${t(
          'Our_algorithm_automatically_judges_your_password_for_sufficient_complexity_As_you_type_you_can_see_the_color_of_bar_change_from_red_to_green_Once_you_have_inputted_a_password_which_is_sufficiently_complex_the_bar_will_be_green',
          'Our algorithm automatically judges your password for sufficient complexity. As you type, you can see the color of bar change from red to green. Once you have inputted a password which is sufficiently complex, the bar will be green',
        )}.`}
        bodyMaxLines={6}
        placement="top-start"
      >
        <BodyLarge color="secondary">{`${t(
          'Hint_complex_passwords_are_atleast_9_characters_long',
          'Hint - complex passwords are atleast 9 characters long',
        )}...`}</BodyLarge>
      </Tooltip>
    </div>
  );
};
